import { useId } from '@chakra-ui/hooks';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpIcon,
} from '@chakra-ui/icons';
import { Box, Collapse, Link, Text } from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { FacilityTreeNode } from 'types/umbraco';
import { colors } from '../../constants/constants';

interface FacilitySummary {
  key: string;
  name: string;
  url?: string;
}

interface GroupedFacilities {
  lan: FacilityTreeNode;
  facilities: FacilitySummary[];
}

interface FacilityListProps {
  facilityTree?: FacilityTreeNode[];
  idPrefix?: string;
}

function sortByName(a: any, b: any): number {
  return a.name.localeCompare(b.name, 'sv');
}

function collectFacilities(node: FacilityTreeNode): FacilitySummary[] {
  const own = node.isFacility
    ? [
        {
          key: node.path,
          name: node.name,
          url: node.path,
        },
      ]
    : [];

  const nested = (node.children || []).flatMap((child) =>
    collectFacilities(child),
  );
  return [...own, ...nested];
}

function FacilityGrid({
  groupedByLan,
}: {
  groupedByLan: GroupedFacilities[];
}): JSX.Element {
  return (
    <Box
      as="ul"
      display="grid"
      gridTemplateColumns={{
        base: '1fr',
        md: 'repeat(2, minmax(0, 1fr))',
        xl: 'repeat(5, minmax(0, 1fr))',
      }}
      borderColor={colors.neutrals[100]}
      listStyleType="none"
      m={0}
      p={0}>
      {groupedByLan.length === 0 && (
        <Text color={colors.neutrals[900]} gridColumn="1 / -1">
          Inga län hittades.
        </Text>
      )}
      {groupedByLan.map((group, index) => (
        <Box
          as="li"
          key={group.lan.path}
          position="relative"
          _before={{
            content: '\"\"',
            display: { base: 'block', xl: 'none' },
            position: 'absolute',
            top: 0,
            right: 8,
            left: 8,
            borderTopWidth: '1px',
            borderColor: colors.neutrals[100],
          }}
          _after={{
            content: '\"\"',
            display: {
              base: index === groupedByLan.length - 1 ? 'block' : 'none',
              xl: 'none',
            },
            position: 'absolute',
            right: 8,
            bottom: 0,
            left: 8,
            borderTopWidth: '1px',
            borderColor: colors.neutrals[100],
          }}
          borderLeftWidth={{ base: 0, xl: '1px' }}
          borderRightWidth={{
            base: 0,
            xl:
              (index + 1) % 5 === 0 || index === groupedByLan.length - 1
                ? '1px'
                : 0,
          }}
          borderColor={colors.neutrals[100]}
          px={8}
          py={{ base: 5, xl: 1 }}
          minW={0}>
          <Link
            href={group.lan.path || '#'}
            display="inline-flex"
            alignItems="center"
            color={colors.neutrals[900]}
            fontSize="sm"
            fontWeight="bold"
            mb={2}>
            {group.lan.name}
            <ChevronRightIcon boxSize={5} ml={1} aria-hidden="true" />
          </Link>

          {group.facilities.length > 0 ? (
            <Box
              as="ul"
              display="flex"
              flexDirection="column"
              gap={0.5}
              listStyleType="none"
              m={0}
              p={0}>
              {group.facilities.map((facility) => (
                <Box as="li" key={facility.key}>
                  <Link
                    href={facility.url || '#'}
                    display="inline-flex"
                    alignItems="center"
                    minH={6}
                    color={colors.neutrals[900]}
                    fontSize="sm"
                    lineHeight="short">
                    {facility.name}
                  </Link>
                </Box>
              ))}
            </Box>
          ) : (
            <Text color={colors.neutrals[900]} fontSize="sm">
              Inga anläggningar hittades under detta län.
            </Text>
          )}
        </Box>
      ))}
    </Box>
  );
}

export default function FacilityList({
  facilityTree,
  idPrefix = 'facility-list',
}: FacilityListProps): JSX.Element {
  const lanNodes = facilityTree || [];
  const reactId = useId();
  const [isExpanded, setIsExpanded] = useState(false);
  const mobileListId = `${idPrefix}-${reactId.replace(/:/g, '')}-mobile`;

  const groupedByLan = useMemo((): GroupedFacilities[] => {
    return lanNodes.sort(sortByName).map((lan) => {
      const facilities = collectFacilities(lan).sort(sortByName);
      return {
        lan,
        facilities,
      };
    });
  }, [lanNodes]);

  return (
    <Box mb={10}>
      <Box
        display={{ base: 'block', md: 'none' }}
        borderTopWidth="1px"
        borderBottomWidth={isExpanded ? 0 : '1px'}
        borderColor={colors.neutrals[100]}>
        <button
          type="button"
          onClick={() => setIsExpanded((expanded) => !expanded)}
          aria-expanded={isExpanded}
          aria-controls={mobileListId}
          style={{
            alignItems: 'center',
            background: 'transparent',
            border: 0,
            color: colors.neutrals[900],
            cursor: 'pointer',
            display: 'flex',
            fontSize: '0.875rem',
            fontWeight: 'bold',
            justifyContent: 'space-between',
            padding: '1rem',
            width: '100%',
          }}>
          Anläggningar per län
          {isExpanded ? (
            <ChevronUpIcon boxSize={6} aria-hidden="true" />
          ) : (
            <ChevronDownIcon boxSize={6} aria-hidden="true" />
          )}
        </button>
      </Box>

      <Box display={{ base: 'block', md: 'none' }}>
        <Collapse in={isExpanded} animateOpacity>
          <Box id={mobileListId}>
            <FacilityGrid groupedByLan={groupedByLan} />
          </Box>
        </Collapse>
      </Box>

      <Box display={{ base: 'none', md: 'block' }}>
        <FacilityGrid groupedByLan={groupedByLan} />
      </Box>
    </Box>
  );
}
